import { useState } from 'react';

import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Permission } from '../../../../../../../../backend/src/auth/permissions/permission';
import { RunStatus } from '../../../../../../../../backend/src/types/types';
import { SettingsHeader } from '../components/settings-header';
import { useSubmitRunApproval } from '@/api/mutations/useSubmitRunApproval';
import { useGetMatterRuns } from '@/api/queries/useGetMatterRuns';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { Run } from '@/api/types/backend-dto-types';
import formatDate from '@/helpers/formatDate';
import { getRunType } from '@/helpers/runs';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { RunApprovalPopoverContent } from '@/new/components/run-approval-popover-content';
import { Badge } from '@/new/components/shadcn/badge';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent } from '@/new/components/shadcn/dialog';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/new/components/shadcn/tooltip';
import { ScrollableTable } from '@/new/components/table/scrollable-table';
import { TableNullField } from '@/new/components/table/table-null-field';

const getStatusDisplay = (run: Run) => {
  if (run.status === RunStatus.AwaitingApproval) {
    return {
      text: 'Awaiting Approval',
      icon: <Icon className="lucide--shield-alert h-4 w-4 text-amber-600" />,
    };
  }
  if (run.status === RunStatus.Failed) {
    return {
      text: run.status ? run.status.charAt(0).toUpperCase() + run.status.slice(1) : 'Failed',
      icon: <Icon className="lucide--x-circle h-4 w-4 text-red-500" />,
    };
  }
  if (run.completedAt) {
    const status = run.status?.toLowerCase() || '';
    if (
      status.includes('fail') ||
      (run.failedPagesCount && run.failedPagesCount > 0 && run.totalPages === run.failedPagesCount)
    ) {
      return {
        text: run.status ? run.status.charAt(0).toUpperCase() + run.status.slice(1) : 'Failed',
        icon: <Icon className="lucide--x-circle h-4 w-4 text-red-500" />,
      };
    }
    return {
      text: run.status ? run.status.charAt(0).toUpperCase() + run.status.slice(1) : 'Completed',
      icon: <Icon className="lucide--check-circle h-4 w-4 text-green-500" />,
    };
  }

  if (run.inQueue) {
    const queuePosition = run.queuePosition !== null ? `, position ${run.queuePosition + 1}` : '';
    return {
      text: `In queue${queuePosition}`,
      icon: <Icon className="lucide--loader-circle h-4 w-4 animate-spin text-amber-500 duration-700" />,
    };
  }
  return {
    text: run.status ? run.status.charAt(0).toUpperCase() + run.status.slice(1) : 'Processing',
    icon: <Icon className="lucide--loader-circle h-4 w-4 animate-spin text-amber-500 duration-700" />,
  };
};

const formatNumber = (num: number) => {
  return num.toLocaleString();
};

const fallbackTableData: Run[] = [];
const columnHelper = createColumnHelper<Run>();

export function RunHistory() {
  const { t } = useTranslation();
  const { matterId } = useParams();
  const { hasPermission } = useHasPermission();

  // Check if user has permission to approve runs
  const canApproveRuns = hasPermission(Permission.ApproveRuns);

  if (!matterId) return null;

  const [globalFilter, setGlobalFilter] = useState('');
  const [selectedRun, setSelectedRun] = useState<Run | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data: matter } = useGetMatterV2(matterId);
  const allDocumentsCaseId = matter?.allDocumentsCaseId ?? undefined;
  const { mutateAsync: submitRunApproval, isLoading: isSubmittingApproval } = useSubmitRunApproval(
    allDocumentsCaseId ?? '',
    matterId,
  );

  const { data: runs, isLoading } = useGetMatterRuns(matterId);

  const handleApprove = async () => {
    if (!selectedRun) return;
    await submitRunApproval({ runId: selectedRun.id, approvalGranted: true });
    setIsDialogOpen(false);
    setSelectedRun(null);
  };

  const handleDeny = async () => {
    if (!selectedRun) return;
    await submitRunApproval({ runId: selectedRun.id, approvalGranted: false });
    setIsDialogOpen(false);
    setSelectedRun(null);
  };

  const handleStatusClick = (run: Run) => {
    if (run.status === RunStatus.AwaitingApproval) {
      setSelectedRun(run);
      setIsDialogOpen(true);
    }
  };

  const columns = [
    columnHelper.display({
      id: 'index',
      header: () => <span>#</span>,
      cell: (info) => (
        <div className="flex h-7 w-7 items-center justify-center rounded bg-gray-100 text-xs font-semibold text-gray-700">
          {runs ? runs.length - info.row.index : '-'}
        </div>
      ),
    }),
    columnHelper.display({
      id: 'status',
      header: () => <span>Status</span>,
      cell: (info) => {
        const statusDisplay = getStatusDisplay(info.row.original);
        const run = info.row.original;
        const isAwaitingApproval = run.status === RunStatus.AwaitingApproval;
        return (
          <button
            onClick={() => handleStatusClick(run)}
            className={`flex items-center gap-2 transition-opacity ${
              isAwaitingApproval ? 'cursor-pointer hover:opacity-80' : 'cursor-default'
            }`}
          >
            {statusDisplay.icon}
            <span className="text-xs font-semibold text-gray-900">{statusDisplay.text}</span>
          </button>
        );
      },
    }),

    columnHelper.accessor('case', {
      header: () => <span>Subset</span>,
      cell: (info) => {
        const caseData = info.getValue();
        return caseData ? (
          <div className="flex items-center">
            <Tooltip>
              <TooltipTrigger>
                <p className="max-w-36 truncate rounded-md text-xs">{caseData.name}</p>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">{caseData.name}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        ) : (
          <span className="text-xs text-gray-400">All Documents</span>
        );
      },
    }),
    columnHelper.accessor('type', {
      header: () => <span>Type</span>,
      cell: (info) => (
        <Badge className="rounded-md border-transparent bg-gray-50 text-gray-700 ring-1 ring-gray-200">
          {getRunType(info.row.original, !info.row.original.case)}
        </Badge>
      ),
    }),
    columnHelper.accessor('createdBy.email', {
      header: () => <span>Submitted By</span>,
      cell: (info) => {
        const email = info.getValue();
        return email ? <span className="text-xs text-gray-900">{email}</span> : <TableNullField />;
      },
    }),
    columnHelper.accessor('createdDate', {
      header: () => <span>Started At</span>,
      cell: (info) => (
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <Icon className="lucide--clock h-3 w-3" />
          <span>{formatDate(info.getValue(), true, true)}</span>
        </div>
      ),
    }),
    columnHelper.accessor('completedAt', {
      header: () => <span>Completed At</span>,
      cell: (info) => {
        const completedAt = info.getValue();
        return completedAt ? (
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Icon className="lucide--check-circle h-3 w-3 text-green-500" />
            <span>{formatDate(completedAt, true, true)}</span>
          </div>
        ) : (
          <TableNullField />
        );
      },
    }),

    columnHelper.accessor('failedPagesCount', {
      header: () => <span>Failed Pages</span>,
      cell: (info) => {
        const count = info.getValue();
        return count > 0 ? (
          <Button variant="destructive" size="sm" asChild className="group text-xs">
            <Link to={`/matters/${matterId}/settings/run-history/${info.row.original.id}`}>
              <Icon className="lucide--alert-triangle mr-1 h-3 w-3" />
              {formatNumber(count)} pages
              <Icon className="lucide--arrow-right ml-1 h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.25" />
            </Link>
          </Button>
        ) : (
          <TableNullField />
        );
      },
    }),
  ];

  const table = useReactTable({
    data: runs ?? fallbackTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  return (
    <>
      <SettingsHeader
        title="Matter Runs"
        subheading={
          <p className="text-sm text-gray-500">
            View the status of all runs across this matter. Runs are processing jobs that extract facts and{' '}
            {t('analyse')}
            documents.
          </p>
        }
      />

      <ScrollableTable
        table={table}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        plural="runs"
        loading={isLoading}
        className="p-0"
      />

      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setSelectedRun(null);
          }
        }}
      >
        <DialogContent className="w-96 max-w-96 p-0">
          {selectedRun && (
            <RunApprovalPopoverContent
              credits={
                (selectedRun.totalPages as number | undefined) ??
                (selectedRun.successfulPagesCount || 0) + (selectedRun.failedPagesCount || 0)
              }
              durationEstimate={selectedRun.durationEstimate}
              isSubmittingApproval={isSubmittingApproval}
              onApprove={handleApprove}
              onDeny={handleDeny}
              runType={getRunType(selectedRun, !selectedRun.case)}
              subsetName={selectedRun.case?.name}
              canApprove={canApproveRuns}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
